/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Middleware, ReducersMapObject, Store } from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { pluginsReducer } from '@opengeoweb/plugins';
import type { SnackbarModuleStore } from '@opengeoweb/snackbar';
import { snackbarReducer, snackbarListener } from '@opengeoweb/snackbar';
import { soundingsReducer } from '@opengeoweb/soundings';
import type {
  WebMapStateModuleState,
  SynchronizationGroupModuleState,
  UIModuleState,
} from '@opengeoweb/store';
import {
  mapReducer,
  layerReducer,
  syncGroupsReducer,
  loadingIndicatorReducer,
  uiReducer,
  mapListener,
  layersListener,
  syncGroupsListener,
  openlayersListener,
  serviceReducer,
  serviceListener,
} from '@opengeoweb/store';
import { timeSeriesReducer } from '@opengeoweb/time-series';
import type { WorkspaceModuleStore } from './types';
import { viewPresetsReducer } from './viewPresets';
import { viewPresetsListener } from './viewPresets/listener';
import { workspaceReducer } from './workspace';
import { workspaceListener } from './workspace/listener';
import { workspaceListReducer } from './workspaceList';
import { workspaceListListener } from './workspaceList/listener';

export const workspaceReducersMap: ReducersMapObject = {
  webmap: mapReducer,
  services: serviceReducer,
  layers: layerReducer,
  syncGroups: syncGroupsReducer,
  loadingIndicator: loadingIndicatorReducer,
  ui: uiReducer,
  workspace: workspaceReducer,
  workspaceList: workspaceListReducer,
  viewPresets: viewPresetsReducer,
  pluginsState: pluginsReducer,
  timeSeries: timeSeriesReducer,
  soundings: soundingsReducer,
  snackbar: snackbarReducer,
};

export const workspaceMiddlewares: Middleware[] = [
  mapListener.middleware,
  serviceListener.middleware,
  layersListener.middleware,
  syncGroupsListener.middleware,
  workspaceListener.middleware,
  workspaceListListener.middleware,
  viewPresetsListener.middleware,
  snackbarListener.middleware,
  openlayersListener.middleware,
];

const workspaceRootReducer = combineReducers(workspaceReducersMap);

export const createMockStore = (
  mockState?:
    | WebMapStateModuleState
    | WorkspaceModuleStore
    | UIModuleState
    | SynchronizationGroupModuleState
    | SnackbarModuleStore,
): Store =>
  configureStore({
    reducer: workspaceRootReducer,
    preloadedState: mockState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(workspaceMiddlewares),
  });
